import { UserListModal } from '@/components/UserListModal';
import type { UseQueryResult } from '@tanstack/react-query';
import { useOrganizationScanUsers } from '@/hooks/api/useScans';
import type { WidgetTableProps } from '@/components/WidgetTable/types';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { formatTopAlertLabel } from '@kirin/api-client/helpers/alerts';
import { useMemo } from 'react';

interface TopAlertsUserListProps {
  organizationId: string;
  /** Composite `<subPolicyKey>::<type>` title key from the Top Alerts widget. */
  alertName: string;
  /**
   * `alerts_revamp` state, threaded from the widget so this heading names the
   * alert exactly as the clicked row did.
   */
  revampEnabled: boolean;
  onClose: () => void;
  open: boolean;
  dateRange?: { from: string; to: string };
}

type TopAlertUser = {
  userId: string;
  email: string | null;
  name: string | null;
  scanCount: number;
  lastSeenAt: string;
};

const COLUMNS: WidgetTableProps<TopAlertUser>['columns'] = [
  {
    header: 'User',
    id: 'user',
    cell: user => (
      <div>
        <div className="font-medium">{user.name || 'Unknown User'}</div>
        {user.email && (
          <div className="text-sm text-muted-foreground">{user.email}</div>
        )}
      </div>
    ),
  },
  {
    header: 'Scans',
    id: 'scanCount',
    cell: row => (
      <span className="tabular-nums text-right block">{row.scanCount}</span>
    ),
    className: 'text-right w-[80px]',
  },
  {
    header: 'Last Seen',
    id: 'lastSeen',
    cell: row => <RelativeTimeWithTooltip date={row.lastSeenAt} />,
  },
];

type TopAlertsQuery = UseQueryResult<TopAlertUser[]>;

/**
 * Modal component to display a list of users associated a top alert
 */
export const TopAlertsUserList = ({
  organizationId,
  alertName,
  revampEnabled,
  onClose,
  open,
  dateRange,
}: TopAlertsUserListProps) => {
  const scanUsersQuery = useOrganizationScanUsers(
    organizationId,
    alertName || undefined,
    !!organizationId && !!alertName && open,
    dateRange
  );

  // Adapt the ScanUsersResponse-shaped query to the generic UserListModal
  const query = useMemo(() => {
    if (!scanUsersQuery.data?.users) return scanUsersQuery;
    return {
      ...scanUsersQuery,
      data: scanUsersQuery.data?.users?.map(userUsage => ({
        userId: userUsage.user.id,
        email: userUsage.user.email,
        name: userUsage.user.name,
        scanCount: userUsage.scanCount,
        lastSeenAt: userUsage.lastSeenAt,
      })),
    };
  }, [scanUsersQuery]);

  return (
    <UserListModal<TopAlertUser>
      open={open}
      title={
        <>
          Users with{' '}
          <span className="font-medium">
            {formatTopAlertLabel(alertName, revampEnabled)}
          </span>{' '}
          alert
        </>
      }
      onOpenChange={isOpen => {
        if (!isOpen) {
          onClose();
        }
      }}
      query={query as unknown as TopAlertsQuery}
      columns={COLUMNS}
    />
  );
};
